import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { QrCodeIcon, Check, X, Crown, Star, ArrowRight } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function Compare() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSubscriptionUpgrade = async (planName) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (planName === 'Free') {
      toast.info('You are already on the free plan');
      return;
    }

    try {
      await api.upgradeSubscription(planName.toLowerCase());
      toast.success(`Successfully upgraded to ${planName} plan!`);
      // Redirect to dashboard to see new features
      navigate('/dashboard');
    } catch (error) {
      console.error('Subscription upgrade failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upgrade subscription. Please try again.');
    }
  };
  const features = [
    {
      category: "QR Code Management",
      items: [
        { feature: "QR Codes Included", free: "10", basic: "100", premium: "500" },
        { feature: "QR Code Generation", free: true, basic: true, premium: true },
        { feature: "QR Code Customization", free: false, basic: true, premium: true },
        { feature: "Bulk QR Code Creation", free: false, basic: true, premium: true },
        { feature: "QR Code Analytics", free: false, basic: true, premium: true },
        { feature: "Custom QR Designs", free: false, basic: true, premium: true },
        { feature: "Bulk Code Registrations", free: false, basic: true, premium: true },
        { feature: "QR Code Templates", free: false, basic: false, premium: true },
        { feature: "White-label QR Codes", free: false, basic: false, premium: true }
      ]
    },
    {
      category: "Messaging & Communication",
      items: [
        { feature: "Messages per Month", free: "10", basic: "100", premium: "500" },
        { feature: "Anonymous Messaging", free: true, basic: true, premium: true },
        { feature: "Two-way Communication", free: true, basic: true, premium: true },
        { feature: "Message History", free: true, basic: true, premium: true },
        { feature: "Message Search", free: false, basic: true, premium: true },
        { feature: "Advanced Message Filtering", free: false, basic: true, premium: true },
        { feature: "Auto-response Templates", free: false, basic: false, premium: true },
        { feature: "Message Threading", free: false, basic: false, premium: true },
        { feature: "Message Analytics", free: false, basic: true, premium: true }
      ]
    },
    {
      category: "Notification System",
      items: [
        { feature: "Email Notifications", free: "1 per QR", basic: "2 per QR", premium: "5 per QR" },
        { feature: "SMS Notifications", free: "0", basic: "2 per QR", premium: "5 per QR" },
        { feature: "Total Notification Contacts", free: "1", basic: "4", premium: "10" },
        { feature: "Priority Notifications", free: false, basic: true, premium: true },
        { feature: "Custom Notification Templates", free: false, basic: false, premium: true },
        { feature: "Notification Scheduling", free: false, basic: false, premium: true },
        { feature: "Real-time Notifications", free: true, basic: true, premium: true }
      ]
    },
    {
      category: "Business Features", 
      items: [
        { feature: "Team Collaboration", free: "1 user", basic: "2 users", premium: "5 users" },
        { feature: "Admin Dashboard", free: false, basic: false, premium: true },
        { feature: "Advanced Analytics", free: false, basic: "Basic", premium: "Advanced" },
        { feature: "Custom Branding", free: false, basic: false, premium: true },
        { feature: "Data Export", free: false, basic: "Limited", premium: "Full" },
        { feature: "API Access", free: false, basic: false, premium: true },
        { feature: "Webhook Integration", free: false, basic: false, premium: true },
        { feature: "Custom Domains", free: false, basic: false, premium: true }
      ]
    },
    {
      category: "Support & Services",
      items: [
        { feature: "Customer Support", free: "Community", basic: "Email", premium: "24/7 Priority" },
        { feature: "Response Time", free: "48+ hours", basic: "24 hours", premium: "4 hours" },
        { feature: "Phone Support", free: false, basic: false, premium: true },
        { feature: "Dedicated Account Manager", free: false, basic: false, premium: true },
        { feature: "Setup Assistance", free: false, basic: false, premium: true },
        { feature: "Training Sessions", free: false, basic: false, premium: true }
      ]
    },
    {
      category: "Physical Products",
      items: [
        { feature: "Storefront Access", free: true, basic: true, premium: true },
        { feature: "Physical QR Stickers", free: "Full price", basic: "20% discount", premium: "20% discount" },
        { feature: "Bulk Ordering", free: false, basic: true, premium: true },
        { feature: "Custom Sticker Designs", free: false, basic: false, premium: true },
        { feature: "Priority Shipping", free: false, basic: false, premium: true }
      ]
    }
  ];

  const plans = [
    { name: "Free", price: "$0", period: "forever", color: "gray", popular: false },
    { name: "Basic", price: "$10", period: "per year", color: "blue", popular: true },
    { name: "Premium", price: "$49", period: "per year", color: "purple", popular: false }
  ];

  const renderFeatureValue = (value, planName) => {
    if (value === true) {
      return <Check className="h-5 w-5 text-green-600 mx-auto" />;
    } else if (value === false) {
      return <X className="h-5 w-5 text-gray-400 mx-auto" />;
    } else {
      return (
        <span className={`text-sm font-medium ${
          planName === 'Premium' ? 'text-purple-600' : 
          planName === 'Basic' ? 'text-blue-600' : 'text-gray-600'
        }`}>
          {value}
        </span>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/pricing" className="text-gray-600 hover:text-gray-900">Back to Pricing</Link>
              <Link to="/auth" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Sign In</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Compare Plans
            <span className="block text-yellow-300">Feature by Feature</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            See exactly what's included in each plan to make the best choice for your needs.
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Plan Headers */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-4 bg-gray-50 border-b">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900">Features</h3>
              </div>
              {plans.map((plan) => (
                <div key={plan.name} className={`p-6 text-center relative ${
                  plan.popular ? 'bg-blue-50 border-l-2 border-r-2 border-blue-500' : ''
                }`}>
                  {plan.popular && (
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                        <Star className="h-3 w-3 mr-1" />
                        Most Popular
                      </span>
                    </div>
                  )}
                  <h3 className={`text-xl font-bold mb-2 ${
                    plan.color === 'purple' ? 'text-purple-600' :
                    plan.color === 'blue' ? 'text-blue-600' : 'text-gray-600'
                  }`}>
                    {plan.name}
                    {plan.name === 'Premium' && <Crown className="inline h-5 w-5 ml-1" />}
                  </h3>
                  <div className="mb-2">
                    <span className={`text-3xl font-bold ${
                      plan.color === 'purple' ? 'text-purple-600' :
                      plan.color === 'blue' ? 'text-blue-600' : 'text-gray-600'
                    }`}>{plan.price}</span>
                    <span className="text-gray-500 ml-1">/{plan.period}</span>
                  </div>
                  <button 
                    onClick={() => handleSubscriptionUpgrade(plan.name)}
                    className={`w-full py-2 px-4 rounded-lg font-semibold transition-colors ${
                      plan.color === 'purple' ? 'bg-purple-600 hover:bg-purple-700 text-white' :
                      plan.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                      'bg-gray-600 hover:bg-gray-700 text-white'
                    }`}
                  >
                    {plan.name === 'Free' ? 'Get Started' : `Choose ${plan.name}`}
                  </button>
                </div>
              ))}
            </div>

            {/* Feature Categories */}
            {features.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                {/* Category Header */}
                <div className="grid grid-cols-4 bg-gray-100 border-b">
                  <div className="col-span-4 p-4">
                    <h4 className="text-lg font-semibold text-gray-800">{category.category}</h4>
                  </div>
                </div>

                {/* Features */}
                {category.items.map((item, itemIndex) => (
                  <div key={itemIndex} className={`grid grid-cols-4 border-b hover:bg-gray-50 ${
                    itemIndex % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                  }`}>
                    <div className="p-4">
                      <span className="text-gray-900 font-medium">{item.feature}</span>
                    </div>
                    <div className="p-4 text-center border-l">
                      {renderFeatureValue(item.free, 'Free')}
                    </div>
                    <div className="p-4 text-center border-l bg-blue-25">
                      {renderFeatureValue(item.basic, 'Basic')}
                    </div>
                    <div className="p-4 text-center border-l">
                      {renderFeatureValue(item.premium, 'Premium')}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-12 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Get Started?</h3>
            <p className="text-lg text-gray-600 mb-8">
              Choose the plan that fits your needs. You can always upgrade later.
            </p>
            <div className="flex justify-center space-x-4">
              <Link 
                to="/pricing"
                className="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center"
              >
                View Pricing <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link 
                to="/auth"
                className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Frequently Asked Questions</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Can I change plans anytime?</h4>
                <p className="text-gray-600">Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and you'll be billed pro-rata.</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">What happens to my data if I downgrade?</h4>
                <p className="text-gray-600">Your data is always safe. If you exceed limits after downgrading, you'll be notified and can upgrade again or remove excess items.</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Do you offer refunds?</h4>
                <p className="text-gray-600">Yes, we offer a 30-day money-back guarantee on all paid plans. No questions asked.</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Is there a setup fee?</h4>
                <p className="text-gray-600">No setup fees ever. The price you see is what you pay, with no hidden costs or surprises.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}